import CheckIcon from "@mui/icons-material/Check";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid-pro";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { checkLabel } from "../models/checkLabels";
import { CheckStatus, FleetCheckRow, REPORTED_CHECKS } from "../models/fleetCheckRow";
import { describeCount } from "../utilities/describeCount";
import { formatOdometerNumber } from "@/pages/live-data/utilities/checklistValues";

type OdometerField = "rawOdometer" | "odometer" | "gpsOdometer";

const STATUS_COLOURS: Record<CheckStatus, "success" | "warning" | "error" | "default"> = {
  notScanned: "default",
  ok: "success",
  warn: "warning",
  alert: "error",
};

/**
 * The columns of the fleet-wide check.
 *
 * The eleven yes/no columns are generated from `REPORTED_CHECKS` rather than written out: they
 * differ only in which check they show, and the original repeated the same definition eleven
 * times, so a change to how a tick renders had to be made eleven times too.
 */
/**
 * @param odometerDecimals - Decimals on the odometer columns.
 * @param withGpsOdometer - Shows the GPS odometer, which the scan only reads when switched on.
 */
export function useFleetCheckColumns(
  odometerDecimals: number,
  withGpsOdometer = false,
): GridColDef<FleetCheckRow>[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        field: "name",
        headerName: t("vehicleDataCheck.columns.asset"),
        minWidth: 200,
        flex: 1.3,
        renderCell: ({ row }) => <AssetCell row={row} />,
      },
      {
        field: "isCommunicating",
        headerName: t("vehicleDataCheck.communicating"),
        minWidth: 110,
        // Not a boolean column: its filter files an unknown value under "No", which would claim
        // that every vehicle not yet looked up does not report.
        type: "singleSelect",
        valueOptions: communicationOptions(t),
        valueGetter: (value: boolean | undefined) => communicationOption(value),
        valueFormatter: (value: CommunicationOption) =>
          communicationOptions(t).find(option => option.value === value)?.label,
        renderCell: ({ row }) => <CommunicatingCell communicating={row.isCommunicating} />,
      },
      {
        field: "licensePlate",
        headerName: t("liveData.checklist.licensePlate"),
        minWidth: 130,
      },
      {
        field: "serial",
        headerName: t("vehicleDataCheck.columns.serial"),
        minWidth: 150,
      },
      { field: "vin", headerName: t("vin"), minWidth: 170 },
      {
        field: "vinDecoded",
        headerName: t("vehicleDataCheck.columns.vinDecoded"),
        minWidth: 180,
        flex: 1,
      },
      {
        field: "lastRecordAt",
        headerName: t("vehicleDataCheck.columns.lastRecordAt"),
        minWidth: 160,
        type: "dateTime",
        valueGetter: (value: Date | null) => value ?? undefined,
      },
      odometerColumn("rawOdometer", t("liveData.checklist.rawOdometer"), t, odometerDecimals),
      odometerColumn("odometer", t("liveData.checklist.odometer"), t, odometerDecimals),
      ...(withGpsOdometer
        ? [
            odometerColumn(
              "gpsOdometer",
              t("vehicleDataCheck.columns.gpsOdometer"),
              t,
              odometerDecimals,
            ),
          ]
        : []),
      ...REPORTED_CHECKS.map(
        (check): GridColDef<FleetCheckRow> => ({
          field: check,
          headerName: checkLabel(check, t),
          minWidth: 110,
          type: "boolean",
          valueGetter: (_, row) => row.reported[check],
          valueFormatter: (value: boolean) => yesNo(value, t),
          renderCell: ({ value }) => <ReportedCell reported={!!value} />,
        }),
      ),
      {
        field: "firmware",
        headerName: t("liveData.checklist.firmware"),
        minWidth: 220,
        flex: 1,
      },
      {
        field: "harness",
        headerName: t("vehicleDataCheck.columns.harness"),
        minWidth: 150,
      },
      {
        field: "fmsStandardSoftwareVersion",
        headerName: t(
          "liveData.checklist.fmsStandardSoftwareVersion",
          "Generic FMS standard software version",
        ),
        minWidth: 220,
      },
      {
        field: "canProtocols",
        headerName: t("vehicleDataCheck.columns.canProtocols"),
        minWidth: 220,
        flex: 1,
        valueGetter: (_, row) => describeCount(row.canProtocols),
      },
      {
        field: "ioxDevices",
        headerName: t("liveData.checklist.ioxDevices"),
        minWidth: 220,
        flex: 1,
        valueGetter: (_, row) => describeCount(row.ioxDevices),
      },
      {
        field: "status",
        headerName: t("vehicleDataCheck.columns.status"),
        minWidth: 120,
        valueGetter: (_, row) => t(`vehicleDataCheck.status.${row.status}`, row.status),
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={STATUS_COLOURS[row.status]}
            label={t(`vehicleDataCheck.status.${row.status}`, row.status)}
          />
        ),
      },
    ],
    // `t` changes identity when the language does; the decimals and the GPS column are the user's.
    [t, odometerDecimals, withGpsOdometer],
  );
}

const yesNo = (value: boolean, t: TFunction) => (value ? t("common.yes") : t("common.no"));

type CommunicationOption = "yes" | "no" | "unknown";

const communicationOption = (value: boolean | undefined): CommunicationOption =>
  value === undefined ? "unknown" : value ? "yes" : "no";

const communicationOptions = (t: TFunction): { value: CommunicationOption; label: string }[] => [
  { value: "yes", label: t("common.yes") },
  { value: "no", label: t("common.no") },
  { value: "unknown", label: t("vehicleDataCheck.notKnown") },
];

function odometerColumn(
  field: OdometerField,
  headerName: string,
  t: TFunction,
  decimals: number,
): GridColDef<FleetCheckRow> {
  return {
    field,
    headerName,
    minWidth: 160,
    type: "number",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => <OdometerCell row={row} field={field} t={t} decimals={decimals} />,
  };
}

/** The vehicle, with a dot saying whether its telematics unit is talking to Geotab right now. */
function AssetCell({ row }: { row: FleetCheckRow }) {
  const { t } = useTranslation();
  const label =
    row.isCommunicating === undefined
      ? t("vehicleDataCheck.notKnown")
      : row.isCommunicating
        ? t("vehicleDataCheck.communicating")
        : t("deviceStatus.activity.offline");

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Tooltip title={label}>
        <FiberManualRecordIcon
          titleAccess={label}
          color={row.isCommunicating ? "success" : "disabled"}
          sx={{ fontSize: 10, flexShrink: 0 }}
        />
      </Tooltip>
      <Typography variant="body2" noWrap>
        {row.name}
      </Typography>
    </Stack>
  );
}

/**
 * Whether the unit talks to Geotab. Unknown is its own state: with no group selected the status
 * is never looked up, and a cross there would claim the vehicle does not report.
 */
function CommunicatingCell({ communicating }: { communicating: boolean | undefined }) {
  const { t } = useTranslation();

  if (communicating === undefined) {
    const label = t("vehicleDataCheck.notKnown");
    return (
      <Tooltip title={label}>
        <span aria-label={label}>–</span>
      </Tooltip>
    );
  }
  const label = yesNo(communicating, t);
  return communicating ? (
    <CheckIcon color="success" fontSize="small" titleAccess={label} />
  ) : (
    <CloseIcon color="error" fontSize="small" titleAccess={label} />
  );
}

function ReportedCell({ reported }: { reported: boolean }) {
  const { t } = useTranslation();
  const label = reported ? t("vehicleDataCheck.reported") : t("liveData.checklist.notFound");

  return reported ? (
    <CheckCircleOutlineIcon color="success" fontSize="small" titleAccess={label} />
  ) : (
    <HelpOutlineIcon color="disabled" fontSize="small" titleAccess={label} />
  );
}

/**
 * An odometer, marked where the two odometers disagree.
 *
 * The warning sits on both of them, because which of the two is wrong is exactly what is not
 * known — only that they cannot both be right.
 */
function OdometerCell({
  row,
  field,
  t,
  decimals,
}: {
  row: FleetCheckRow;
  field: OdometerField;
  t: TFunction;
  decimals: number;
}) {
  const { i18n } = useTranslation();
  const value = row[field];
  const mismatchLabel = t(
    "vehicleDataCheck.odometerMismatch",
    "The raw odometer and the odometer do not agree",
  );

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ width: "100%" }}
    >
      <Typography variant="body2">
        {formatOdometerNumber(value, unitOf(row, field), i18n.language, decimals)}
      </Typography>
      {row.odometerMismatch && field !== "gpsOdometer" && (
        <Tooltip title={mismatchLabel}>
          <WarningAmberIcon color="warning" fontSize="small" titleAccess={mismatchLabel} />
        </Tooltip>
      )}
    </Stack>
  );
}

/** Each odometer carries its own unit; the profile can cover one diagnostic and not the other. */
function unitOf(row: FleetCheckRow, field: OdometerField): string {
  const units: Record<OdometerField, string> = {
    rawOdometer: row.rawOdometerUnit,
    odometer: row.odometerUnit,
    gpsOdometer: row.gpsOdometerUnit,
  };
  return units[field];
}
